import React from 'react';
import { Check as CheckIcon } from '@mui/icons-material';

import {
    OrderType,
    OrderStatusType,
    WatchlistType,
    WatchlistForListType
} from '../types';
import { FAIRTRANSPORT_MEMBERSHIP_TYPES } from '../types/FairtransportMembershipType';

type DateType = 'datetime' | 'time';

function getTzDate(date: Date) {
    const time = date.getTime();
    const tzOffset = date.getTimezoneOffset() * 60 * 1000;

    return new Date(time - tzOffset);
}

function formatDateDateTime(input: unknown, dateType?: DateType) {
    if (!(input instanceof Date || typeof input === 'string')) {
        return '-';
    }
    const date = input instanceof Date ? input : new Date(input);

    if (Number.isNaN(date.getTime())) {
        return '-';
    }

    const dateString = (getTzDate(date)).toISOString();

    if (dateType === 'datetime') {
        return dateString.slice(0, 16).replace('T', ' ');
    }

    if (dateType === 'time') {
        return dateString.slice(11, 16);
    }

    return dateString.slice(0, 10);
}

export const formatSubscriptionDate = (
    { endDate, isActive, isCarrier }
        : { endDate: string | null, isActive: boolean, isCarrier: boolean }
): string => {
    if (!isCarrier) {
        return '-';
    }

    const endDateFormatted = formatDateDateTime(endDate);

    if (endDateFormatted === '-') {
        return 'nej';
    }
    return isActive
        ? `giltigt t.o.m\n${endDateFormatted}`
        : `löpte ut\n${endDateFormatted}`;
};

export const formatDate = (s: unknown) => formatDateDateTime(s);
export const formatDateTime = (s: unknown) => formatDateDateTime(s, 'datetime');
export const formatTime = (s: unknown) => formatDateDateTime(s, 'time');

const msPerDay = 24 * 60 * 60 * 1000;
const msPerHour = 60 * 60 * 1000;
const msPerMinute = 60 * 1000;
const msPerSecond = 1000;

const pad = (n: number) => String(n).padStart(2, '0');

export function getTimeUntil(d?: string | Date) {
    return d ? (new Date(d)).getTime() - (new Date()).getTime() : 0;
}

export function formatTimeLeft(diff: number) {
    const days = Math.floor(diff / (msPerDay));
    const hours = Math.floor((diff - (days * msPerDay)) / msPerHour);
    const minutes = Math.floor((diff - (days * msPerDay) - (hours * msPerHour)) / msPerMinute);
    const seconds = Math.floor(
        (diff - (days * msPerDay) - (hours * msPerHour) - (minutes * msPerMinute)) / msPerSecond
    );

    if (days > 0) {
        return `${days}d ${pad(hours)}t ${pad(minutes)}m ${pad(seconds)}s kvar`;
    }
    if (hours > 0) {
        return `${hours}t ${pad(minutes)}m ${pad(seconds)}s kvar`;
    }
    if (minutes > 0) {
        return `${minutes}m ${pad(seconds)}s kvar`;
    }
    if (seconds > 0) {
        return `${seconds}s kvar`;
    }
    return '0s kvar';
}

export const formatOrderFlag = ({ isDeleted, isArchived }: { isDeleted: boolean, isArchived: boolean }) => {
    if (isDeleted) {
        return 'Borttaget';
    }
    if (isArchived) {
        return 'Arkiverat';
    }
    return '';
};

export const formatOrderStatus = (status: OrderStatusType) => {
    switch (status) {
        case 'DRAFTED':
            return 'Utkast';

        case 'PUBLISHED':
            return 'Publicerad';

        case 'ACCEPTED':
            return 'Tilldelad';

        case 'STARTED':
            return 'Bekräftad';

        case 'COMPLETED':
            return 'Utförd';

        default:
            return '-';
    }
};

export const formatOfferStatus = (offerStatus: string) => {
    switch (offerStatus) {
        case 'ACCEPTED':
            return 'Tilldelat';

        case 'REJECTED':
            return 'Avbjöt';

        case 'EXPIRED':
            return 'Utlöpt';

        case 'PENDING':
            return 'Ohanterat';

        default:
            return '-';
    }
};

export function formatNumber(value: unknown, unit?: string) {
    if (typeof value !== 'number') {
        return '-';
    }

    const digits = (Math.round(value * 100) / 100).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1 ');

    return unit
        ? `${digits} ${unit}`
        : digits;
}

export const formatSeconds = (sec: unknown) => {
    if (typeof sec !== 'number') {
        return '-';
    }
    const min = Math.floor(sec / 60);
    const hours = Math.floor(min / 60);

    return hours
        ? `${hours} h ${min % 60} min`
        : `${min} min`;
};

export const formatMeters = (meters: unknown) => (
    typeof meters === 'number'
        ? formatNumber(Math.floor(meters / 1000), 'km')
        : '-'
);

export function formatRange(low: number | null, high: number | null, unit?: string) {
    if (low !== null && high !== null) {
        return `${low} - ${high} ${unit}`;
    }
    if (low !== null && high === null) {
        return `Mer än ${low} ${unit}`;
    }
    if (low === null && high !== null) {
        return `Mindre än ${high} ${unit}`;
    }
    return '';
}

export const formatBool = (val: any) => (
    val ? <CheckIcon fontSize="small" /> : null
);

export const formatYesNo = (val: any) => (
    val ? 'Ja' : 'Nej'
);

export function formatGroupRole(
    { isOwner, isAccepted, isInvited }
        : { isOwner: boolean, isAccepted: boolean, isInvited: boolean }
) {
    if (isOwner) {
        return 'Administratör';
    }
    if (isAccepted) {
        return 'Medlem';
    }
    if (isInvited) {
        return 'Inbjuden';
    }
    return 'Ej medlem';
}

export function formatGroupType({ isPublic }: { isPublic: boolean }) {
    return (isPublic ? 'Publik' : 'Privat');
}

export function formatLoadArray(item: { [key: string]: any }) {
    return [
        !!item.grossWeight && formatNumber(Number(item.grossWeight), 'kg tot'),
        !!item.chargeableWeight && formatNumber(Number(item.chargeableWeight), 'kg fdr'),
        !!item.volume && formatNumber(Number(item.volume), 'm³'),
        !!item.loadingMeters && formatNumber(Number(item.loadingMeters), 'flm'),
        !!item.palletPlaces && formatNumber(Number(item.palletPlaces), 'ppl'),
        !!item.pallets && formatNumber(Number(item.pallets), 'p')
    ].filter((v) => v);
}

type OrderDateTimeType = {
    firstDate: string,
    lastDate: string | null,
    firstTime: string | null,
    lastTime: string | null
};

function getOrderDateTime({
    firstDate,
    lastDate,
    firstTime,
    lastTime
}: OrderDateTimeType) {
    const date = formatDate(firstDate) === formatDate(lastDate) || lastDate === null
        ? formatDate(firstDate)
        : `${formatDate(firstDate)} - ${formatDate(lastDate)}`;

    // eslint-disable-next-line no-nested-ternary
    const time = (!firstTime && !lastTime) ? '' : (
        (formatTime(firstTime) === formatTime(lastTime) || lastTime === null)
            ? formatTime(firstTime)
            : `${formatTime(firstTime)} - ${formatTime(lastTime)}`
    );

    return time ? `${date}, ${time}` : date;
}

export function getOrderPickupDateTime(o: OrderType) {
    return getOrderDateTime({
        firstDate: o.firstPickupDate,
        lastDate: o.lastPickupDate,
        firstTime: o.firstPickupTime,
        lastTime: o.lastPickupTime
    });
}

export function getOrderDeliveryDateTime(o: OrderType) {
    return getOrderDateTime({
        firstDate: o.firstDeliveryDate,
        lastDate: o.lastDeliveryDate,
        firstTime: o.firstDeliveryTime,
        lastTime: o.lastDeliveryTime
    });
}

type OrderPlaceType = {
    street: string,
    zip: string,
    city: string
}

function getOrderPlace({ street, zip, city }: OrderPlaceType) {
    return [street, zip, city].filter(Boolean).join(', ');
}

export function getOrderPickupPlace(o: OrderType) {
    return getOrderPlace({
        street: o.pickupStreet,
        zip: o.pickupZip,
        city: o.pickupCity
    });
}

export function getOrderDeliveryPlace(o: OrderType) {
    return getOrderPlace({
        street: o.deliveryStreet,
        zip: o.deliveryZip,
        city: o.deliveryCity
    });
}

export const matchModeLabels = {
    within: 'Hela uppdraget inom området',
    touches: 'Hämtas eller lämnas i området'
};

const sourceLabels = {
    user: 'Användare',
    route: 'Företagsrutt',
    group: 'Grupp',
    residence: 'Hemort',
    signup_default: 'Standard vid registrering'
};

export const GROUP_AREA_LABEL = 'Gruppens uppdrag';

/** A group's watchlist without route or area follows the group's assignments, not the whole country. */
function followsOnlyGroup(w: Partial<Pick<WatchlistType, 'routeId' | 'areaType' | 'groupId'>>) {
    return !!w.groupId && !w.routeId && !w.areaType;
}

export function formatMatchMode(
    w: Pick<WatchlistType, 'matchMode'> & Partial<Pick<WatchlistType, 'routeId' | 'areaType' | 'groupId'>>
) {
    return followsOnlyGroup(w) ? '–' : matchModeLabels[w.matchMode] || w.matchMode || '';
}

export function formatSource(w: Pick<WatchlistType, 'source'>) {
    return sourceLabels[w.source] || w.source || '';
}

export function formatActive(w: Pick<WatchlistType, 'isActive'>) {
    return w.isActive ? 'Aktiv' : 'Pausad';
}

export function formatWatchlistArea(
    w: Pick<WatchlistType, 'routeId' | 'routeName' | 'areaType'> & Partial<Pick<WatchlistType, 'areaShape' | 'groupId'>>
) {
    if (w.routeId) {
        return `Rutt: ${w.routeName || 'Namnlös rutt'}`;
    }
    if (w.areaType === 'circle') {
        const shape = w.areaShape;
        return shape && 'radius' in shape
            ? `Cirkel, ${Math.round(shape.radius / 1000)} km radie`
            : 'Cirkel';
    }
    if (w.areaType === 'polygon') {
        return 'Egen polygon';
    }
    return followsOnlyGroup(w) ? GROUP_AREA_LABEL : 'Hela landet';
}

export function formatType({ isJob, isCapacity, takesOffer }: WatchlistForListType | WatchlistType) {
    const types = [];
    if (isJob) {
        types.push(takesOffer ? 'Uppdrag utan externa uppdrag' : 'Uppdrag');
    }
    if (isCapacity) {
        types.push('Kapacitet');
    }
    return types.join(' & ');
}
export function formatWatchlist(w: WatchlistType) {
    return [
        formatType(w),
        formatWatchlistArea(w),
        formatRange(w.minGrossWeight, w.maxGrossWeight, 'kg tot'),
        formatRange(w.minChargeableWeight, w.maxChargeableWeight, 'kg ldg'),
        formatRange(w.minVolume, w.maxVolume, 'm³'),
        formatRange(w.minLoadingMeters, w.maxLoadingMeters, 'flm'),
        formatRange(w.minPalletPlaces, w.maxPalletPlaces, 'ppl'),
        formatRange(w.minPallets, w.maxPallets, 'pallar'),
        // formatRange(w.minPieces, w.maxPieces, 'stycken'),
        w.groupName ? `I grupp ${w.groupName}` : '',
        w.hasListPrice ? 'Har riktpris' : ''
    ].filter((v) => !!v).join(', ');
}

export function formatPhone(phone: string | null | undefined) {
    if (!phone) {
        return '';
    }
    const prefixes = [
        '011',
        '0120',
        '0121',
        '0122',
        '0123',
        '0125',
        '013',
        '0140',
        '0141',
        '0142',
        '0143',
        '0144',
        '0150',
        '0151',
        '0152',
        '0155',
        '0156',
        '0157',
        '0158',
        '0159',
        '016',
        '0171',
        '0173',
        '0174',
        '0175',
        '0176',
        '018',
        '019',
        '021',
        '0220',
        '0221',
        '0222',
        '0223',
        '0224',
        '0225',
        '0226',
        '0227',
        '023',
        '0240',
        '0241',
        '0243',
        '0246',
        '0247',
        '0248',
        '0250',
        '0251',
        '0253',
        '0258',
        '026',
        '0270',
        '0271',
        '0278',
        '0280',
        '0281',
        '0290',
        '0291',
        '0292',
        '0293',
        '0294',
        '0295',
        '0297',
        '0300',
        '0301',
        '0302',
        '0303',
        '0304',
        '031',
        '0320',
        '0321',
        '0322',
        '0325',
        '033',
        '0340',
        '0345',
        '0346',
        '035',
        '036',
        '0370',
        '0371',
        '0372',
        '0380',
        '0381',
        '0382',
        '0383',
        '0390',
        '0392',
        '0393',
        '040',
        '0410',
        '0411',
        '0413',
        '0414',
        '0415',
        '0416',
        '0417',
        '0418',
        '042',
        '0430',
        '0431',
        '0433',
        '0435',
        '044',
        '0451',
        '0454',
        '0455',
        '0456',
        '0457',
        '0459',
        '046',
        '0470',
        '0471',
        '0472',
        '0474',
        '0476',
        '0477',
        '0478',
        '0479',
        '0480',
        '0481',
        '0485',
        '0486',
        '0490',
        '0491',
        '0492',
        '0493',
        '0494',
        '0495',
        '0496',
        '0498',
        '0499',
        '0500',
        '0501',
        '0502',
        '0503',
        '0504',
        '0505',
        '0506',
        '0510',
        '0511',
        '0512',
        '0513',
        '0514',
        '0515',
        '0520',
        '0521',
        '0522',
        '0523',
        '0524',
        '0525',
        '0526',
        '0528',
        '0530',
        '0531',
        '0532',
        '0533',
        '0534',
        '054',
        '0550',
        '0551',
        '0552',
        '0553',
        '0554',
        '0555',
        '0560',
        '0563',
        '0564',
        '0565',
        '0570',
        '0571',
        '0573',
        '0580',
        '0581',
        '0582',
        '0583',
        '0584',
        '0585',
        '0586',
        '0587',
        '0589',
        '0590',
        '0591',
        '060',
        '0611',
        '0612',
        '0613',
        '0620',
        '0621',
        '0622',
        '0623',
        '0624',
        '063',
        '0640',
        '0642',
        '0643',
        '0644',
        '0645',
        '0647',
        '0650',
        '0651',
        '0652',
        '0653',
        '0657',
        '0660',
        '0661',
        '0662',
        '0663',
        '0670',
        '0671',
        '0672',
        '0680',
        '0682',
        '0684',
        '0687',
        '0690',
        '0691',
        '0692',
        '0693',
        '0695',
        '0696',
        '08',
        '090',
        '0910',
        '0911',
        '0912',
        '0913',
        '0914',
        '0915',
        '0916',
        '0918',
        '0920',
        '0921',
        '0922',
        '0923',
        '0924',
        '0925',
        '0926',
        '0927',
        '0928',
        '0929',
        '0930',
        '0932',
        '0933',
        '0934',
        '0935',
        '0940',
        '0941',
        '0942',
        '0943',
        '0950',
        '0951',
        '0952',
        '0953',
        '0954',
        '0960',
        '0961',
        '0970',
        '0971',
        '0973',
        '0975',
        '0976',
        '0977',
        '0978',
        '0980',
        '0981',
        '070',
        '073',
        '076'
    ];

    const prefix = prefixes.find((p) => phone.startsWith(p));

    if (prefix) {
        const numberPart = phone.slice(prefix.length);
        if (numberPart.length === 7) {
            return `${prefix} - ${numberPart.slice(0, 3)} ${numberPart.slice(3, 5)} ${numberPart.slice(5)}`;
        }
        if (numberPart.length === 6) {
            return `${prefix} - ${numberPart.slice(0, 2)} ${numberPart.slice(2, 4)} ${numberPart.slice(4)}`;
        }
        return `${prefix} - ${numberPart}`;
    }

    return phone;
}

/** Strip IPv4-mapped IPv6 prefix (e.g. ::ffff:1.2.3.4 → 1.2.3.4) for display. */
export function formatIpAddress(ip: string | null | undefined) {
    if (!ip) {
        return '';
    }
    return String(ip).replace(/^::ffff:/i, '');
}

export const formatFairtransportMembership = (membership: string | null) => {
    switch (membership) {
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.MEMBER:
            return 'Medlem';
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL1:
            return 'Medlem (mervärdesnivå 1)';
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL2:
            return 'Medlem (mervärdesnivå 2)';
        case FAIRTRANSPORT_MEMBERSHIP_TYPES.VALUELEVEL3:
            return 'Medlem (mervärdesnivå 3)';
        default:
            return 'Ej medlem';
    }
};

export const formatExternalUrl = (url: string) => {
    const urlFormatted = url.trim().toLowerCase().replace(/(^|[^:])\/\//g, '$1/');
    return urlFormatted.startsWith('http') ? urlFormatted : `https://${urlFormatted}`;
};
